import { ErrorMonitoringService } from "../ErrorMonitoringService";

import {
  DatasetViewingContext,
  FileViewingContext,
  ViewingContext,
  ViewingContextMap,
  ViewingContextSourceName,
  VisualizerViewingContext,
} from "./ViewingContext";

type OnChangeFn = (viewingContext: ViewingContext) => void;
type UnsubscribeFn = () => void;

interface StoredViewingContextPart<T extends ViewingContextSourceName> {
  name: T;
  value: Partial<ViewingContextMap[T]>;
}

// Union type for storage - allows storing any valid name/value pair
type AnyStoredViewingContextPart =
  | StoredViewingContextPart<"dataset">
  | StoredViewingContextPart<"visualizer">
  | StoredViewingContextPart<"file">;

export class ViewingContextService {
  private static instance: ViewingContextService | null = null;
  private subscribers: Set<OnChangeFn> = new Set();
  private viewingContextParts: Map<symbol, AnyStoredViewingContextPart> =
    new Map();
  private cachedContext: ViewingContext | null = null;

  private constructor() {
    // Private to enforce singleton
  }

  public static getInstance(): ViewingContextService {
    if (ViewingContextService.instance === null) {
      ViewingContextService.instance = new ViewingContextService();
    }
    return ViewingContextService.instance;
  }

  // This is used by syncExternalStore thus the setTimeout/setInterval/useEffect style
  // "return an unmount function" pattern.
  public subscribe(subscriber: OnChangeFn): UnsubscribeFn {
    this.subscribers.add(subscriber);

    return () => {
      this.subscribers.delete(subscriber);
    };
  }

  public addViewingContextPart<T extends ViewingContextSourceName>(
    componentId: symbol,
    name: T,
    value: Partial<ViewingContextMap[T]>,
  ): void {
    this.viewingContextParts.set(componentId, {
      name,
      value,
    } as AnyStoredViewingContextPart);
    this.recomputeViewingContextAndNotify();
  }

  public removeViewingContextPart(componentId: symbol): void {
    if (this.viewingContextParts.has(componentId)) {
      this.viewingContextParts.delete(componentId);
    }
    this.recomputeViewingContextAndNotify();
  }

  public getViewingContext(): ViewingContext {
    if (this.cachedContext === null) {
      this.cachedContext = this.computeViewingContext();
    }
    return this.cachedContext;
  }

  private computeViewingContext(): ViewingContext {
    const workingContext: ViewingContext = {};

    // Group parts by name and merge them
    const datasetParts: Partial<DatasetViewingContext>[] = [];
    const visualizerParts: Partial<VisualizerViewingContext>[] = [];
    const fileParts: Partial<FileViewingContext>[] = [];

    for (const part of this.viewingContextParts.values()) {
      if (part.name === "dataset") {
        datasetParts.push(part.value);
      } else if (part.name === "file") {
        fileParts.push(part.value);
      } else if (part.name === "visualizer") {
        visualizerParts.push(part.value);
      }
    }

    // Merge dataset parts. At the time of writing this, selectedFiles is provided in a different place than datasetId
    if (datasetParts.length > 0) {
      const mergedDataset: DatasetViewingContext = {
        datasetId: "",
        selectedFiles: [],
      };
      for (const part of datasetParts) {
        if (part.datasetId !== undefined) {
          mergedDataset.datasetId = part.datasetId;
        }
        if (part.record !== undefined) {
          mergedDataset.record = part.record;
        }
        if (part.selectedFiles !== undefined) {
          mergedDataset.selectedFiles = part.selectedFiles;
        }
      }
      workingContext.dataset = mergedDataset;
    }

    // Take last viz parts. Very likely this should only ever be one part
    if (visualizerParts.length > 0) {
      const mergedVisualizer: VisualizerViewingContext = {
        dataSources: [],
        state: {},
      };
      for (const part of visualizerParts) {
        if (part.dataSources !== undefined) {
          mergedVisualizer.dataSources = part.dataSources;
        }
        if (part.state !== undefined) {
          mergedVisualizer.state = part.state;
        }
      }
      workingContext.visualizer = mergedVisualizer;
    }

    // Take last file parts. It's possible (but weird / unlikely) that the record is
    // provided in one place and the ID in another
    if (fileParts.length > 0) {
      const mergedFile: FileViewingContext = {
        fileId: "",
      };
      for (const part of fileParts) {
        if (part.fileId !== undefined) {
          mergedFile.fileId = part.fileId;
        }
        if (part.record !== undefined) {
          mergedFile.record = part.record;
        }
      }
      workingContext.file = mergedFile;
    }

    return workingContext;
  }

  private notifySubscribers(viewingContext: ViewingContext | null): void {
    if (viewingContext === null) {
      return;
    }

    this.subscribers.forEach((subscriber) => {
      try {
        subscriber(this.cachedContext!);
      } catch (error) {
        ErrorMonitoringService.captureError(error, {
          context: {
            cachedContext: this.cachedContext,
          },
        });
      }
    });
  }

  private recomputeViewingContextAndNotify(): void {
    this.cachedContext = this.computeViewingContext();
    this.notifySubscribers(this.cachedContext);
  }
}

// Initialize
ViewingContextService.getInstance();
